import { useState, useEffect } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// Flattens the nested category tree into a single list, indenting each
// option's label based on depth so the parent/child hierarchy is still
// visible even in a normal (non-tree) dropdown.
const flattenCategories = (categories, depth = 0) => {
    let flat = [];
    categories.forEach((cat) => {
        flat.push({ id: cat._id, label: `${'— '.repeat(depth)}${cat.name}` });
        if (cat.children && cat.children.length > 0) {
            flat = flat.concat(flattenCategories(cat.children, depth + 1));
        }
    });
    return flat;
};

// Reusable multi-select for picking one or more real categories, used by
// both the Add Product and Edit Product forms. `value` is an array of
// category ids; `onChange` receives the updated array of ids.
const CategoryMultiSelect = ({ value, onChange, label = "Categories" }) => {
    const [options, setOptions] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                setOptions(flattenCategories(data.categories));
            } catch (error) {
                console.error('Failed to load categories', error);
            }
        };
        fetchCategories();
    }, []);

    const selectedOptions = options.filter((opt) => value.includes(opt.id));

    return (
        <Autocomplete
            multiple
            size="small"
            options={options}
            disableCloseOnSelect
            value={selectedOptions}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(opt) => opt.label}
            onChange={(e, newValue) => onChange(newValue.map((opt) => opt.id))}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                    <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} style={{ marginRight: 8 }} />
                    {option.label}
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={label} placeholder="Select categories" />}
        />
    );
};

export default CategoryMultiSelect;
